import React, { useRef, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { Box, InputBaseComponentProps, TextField, Typography, Popover, Button } from '@material-ui/core';
import { KeyboardArrowDown } from '@material-ui/icons';
import { useCampfireTheme } from '../../../../../../../theme/useCampfireTheme';

export const CICODateSelect = ({
  time,
  field,
  onChange,
}: {
  time: Date | '';
  field: string;
  onChange: (value: Date) => void;
}) => {
  const { theme, isMobile } = useCampfireTheme();
  const inputRef = useRef<InputBaseComponentProps>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Detect iOS devices
  const isIOS = useMemo(() => {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isIOS) {
      // On iOS, trigger the native time input directly
      const iosInput = document.getElementById(`ios-time-input-${field}`) as HTMLInputElement;
      if (iosInput) {
        // iOS requires the input to be visible and focusable to trigger the picker
        iosInput.style.visibility = 'visible';
        iosInput.style.opacity = '0';
        iosInput.focus();
        iosInput.click();
      }
    } else if (isMobile) {
      // On other mobile devices, show popover
      setAnchorEl(event.currentTarget);
    } else {
      // On desktop, try to use showPicker if available
      try {
        if (inputRef.current?.showPicker) {
          inputRef.current.showPicker();
        } else {
          setAnchorEl(event.currentTarget);
        }
      } catch (e) {
        // Fallback to popover if showPicker fails
        setAnchorEl(event.currentTarget);
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = event.target.value;
    if (timeValue) {
      const newTime = DateTime.fromFormat(timeValue, 'HH:mm').toJSDate();
      onChange(newTime);
      if (isIOS || isMobile) {
        setAnchorEl(null);
      }
    }
  };

  const timeValue = time ? DateTime.fromJSDate(time).toFormat('HH:mm') : '';

  return (
    <Box mr={2} style={{ color: theme.color.secondary.main900, cursor: 'pointer' }} position='relative'>
      <Box display='flex' alignItems='center' onClick={handleClick}>
        <Typography
          style={{ minWidth: 64, marginRight: '8px', fontSize: '13px', fontWeight: 600, textAlign: 'center' }}
        >
          {time ? DateTime.fromJSDate(time).toFormat('h:mm a') : 'Now'}
        </Typography>
        <KeyboardArrowDown style={{ fontSize: '14px' }} />
      </Box>

      {/* iOS: Hidden native time input that gets triggered on click */}
      {isIOS && (
        <input
          type='time'
          value={timeValue}
          onChange={(e) => handleTimeChange(e as any)}
          id={`ios-time-input-${field}`}
          step={300}
          style={{
            visibility: 'hidden',
            position: 'absolute',
            zIndex: 100,
            top: '-28px',
            width: '100px',
            height: '30px',
          }}
        />
      )}

      {/* Desktop: Hidden input for showPicker fallback */}
      {!isIOS && !isMobile && (
        <TextField
          onChange={handleTimeChange}
          variant='outlined'
          type='time'
          label='Start time'
          value={timeValue}
          fullWidth
          name={field}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            step: 300, // 5 min
            ref: inputRef,
          }}
          style={{ visibility: 'hidden', position: 'absolute', zIndex: 100, top: '-28px' }}
        />
      )}

      {/* Non-iOS mobile popover */}
      {!isIOS && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box p={2} minWidth={200}>
            <Typography variant='subtitle2' gutterBottom>
              Select Time
            </Typography>
            <TextField
              type='time'
              value={timeValue}
              onChange={handleTimeChange}
              variant='outlined'
              fullWidth
              size='small'
              inputProps={{
                step: 300, // 5 min
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Box mt={1} display='flex' justifyContent='space-between'>
              <Button size='small' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                size='small'
                color='primary'
                onClick={() => {
                  const now = new Date();
                  onChange(now);
                  handleClose();
                }}
              >
                Now
              </Button>
            </Box>
          </Box>
        </Popover>
      )}
    </Box>
  );
};
